// SUB-OFFICE 7 — the Bureau's furniture that moves, speaks, or listens:
// the live chyron cornices (one texture, every strip), the split-flap
// NOW SERVING board on its 10s stats poll, the dispenser pilot lamp,
// the turnstile, the Vault beacon, and every locked door's cold reply.

import { Color, Group, Mesh, MeshBasicMaterial, PlaneGeometry } from 'three';
import { PAL, shade } from '../palette';
import { SPLITFLAP, DOORS, TURNSTILE, DISPENSER, COPIER } from '../world/layout';
import { Chyron, SplitFlap } from '../render/textures';
import { patchMaterial } from '../render/scene';
import { PLAQUE as PLAQUE_TEXT } from '../../shared/copy';
import type { StatsBlob } from '../../shared/types';
import type { BuiltWorld } from '../render/world';
import type { Entity, EventBus } from './entities';
import type { Player } from './player';

export class Props implements Entity {
  readonly id = 'props';
  readonly group = new Group();
  readonly chyron: Chyron;

  private flap: SplitFlap;
  private world: BuiltWorld;
  private t = 0;
  private turnstileAngle = 0;
  private turnstileTarget = 0;
  private playerWasEast = true;
  private beaconPivot = new Group();
  private vaultMouthOn = 0; // 0 idle .. 1 adjudication (one warm step)
  private reissueT = -1; // copier REISSUE sweep clock; <0 idle

  constructor(private bus: EventBus, world: BuiltWorld, private player: Player, reducedMotion: boolean) {
    this.world = world;

    // --- the chyron: one live texture skins every cornice quad -------------
    this.chyron = new Chyron(reducedMotion);
    const chyronMat = new MeshBasicMaterial({ map: this.chyron.texture });
    patchMaterial(chyronMat, { dimExempt: true }); // the Machine's voice stays lit
    this.group.add(new Mesh(world.chyronGeo, chyronMat));
    this.chyron.onTick = () => bus.emit('audio:tick');
    this.chyron.onLine = (line) => bus.emit('chyron:line', line);
    bus.on('chyron', (p) => this.chyron.print(p as string));

    // --- split-flap NOW SERVING -------------------------------------------
    this.flap = new SplitFlap();
    const flapMat = new MeshBasicMaterial({ map: this.flap.texture });
    patchMaterial(flapMat, { dimExempt: true });
    const flapMesh = new Mesh(new PlaneGeometry(SPLITFLAP.w, SPLITFLAP.h), flapMat);
    flapMesh.position.set(SPLITFLAP.x, SPLITFLAP.y, SPLITFLAP.z);
    this.group.add(flapMesh);
    this.flap.onFlap = () => bus.emit('audio:flap');
    // stats poll drives the board; a dead poll freezes the last value —
    // the Bureau does not announce interruptions of service
    bus.on('stats', (p) => {
      const s = p as StatsBlob;
      if (typeof s.now_serving === 'number') this.flap.setValue(s.now_serving);
    });

    // --- vault beacon: rotating amber lighthouse cone ----------------------
    const beacon = world.beacon;
    if (beacon.parent) beacon.parent.remove(beacon);
    this.beaconPivot.position.copy(beacon.position);
    beacon.position.set(2.0, 0, 0);
    beacon.rotation.set(0, 0, Math.PI / 2);
    this.beaconPivot.add(beacon);
    this.group.add(this.beaconPivot);

    // --- vault mouth: the sense of a mind behind the ring ------------------
    // it warms ONE STEP while the Machine adjudicates, and settles on verdict.
    bus.on('ceremony:adjudication', () => { this.vaultMouthOn = 1; });
    bus.on('ceremony:verdict', () => { this.vaultMouthOn = 0; });

    // --- copier REISSUE: the identity is reproduced; pilots light, lid sweeps.
    // The gameplay director emits this at the reissue beat (kept a flavor hook
    // until then — the same stubbing pattern as the window summons).
    bus.on('copier:reissue', () => { this.reissueT = 0; });

    // --- interactions ------------------------------------------------------
    bus.on('interact', (p) => this.onInteract((p as { id: string }).id));
  }

  private onInteract(id: string): void {
    // dispenser / form3 / form1desk / lectern / drawers / desks / tubemouth /
    // copier / bench:4 / windows are owned by the gameplay director (game.ts);
    // Props keeps only the furniture that never graduates past flavor.
    switch (id) {
      case 'plaque':
        this.bus.emit('toast', PLAQUE_TEXT);
        break;
      // gallery / board / podiums / drawers are owned by the gameplay director
      case 'turnstile':
        this.bus.emit('chyron', 'THE TURNSTILE TURNS FOR THOSE WHO PROCEED. PROCEED.');
        break;
      default: {
        if (id.startsWith('door:')) {
          // Room 0: when the seam is lit the gameplay director owns this door
          if (id === 'door:form99' && document.body.dataset.room0 === '1') return;
          // the working lift is a live transition, not a locked-door line —
          // the gameplay director owns it (routes into the vertical core).
          if (id === 'door:lift') return;
          const door = DOORS.find((d) => 'door:' + d.id === id);
          if (door) {
            this.bus.emit('chyron', door.line);
            this.bus.emit('audio:thunk');
          }
        } else if (id.startsWith('bench:') && id !== 'bench:4') {
          this.bus.emit('chyron', 'THE BENCH ACCEPTS YOU IN PRINCIPLE. SITTING IS SCHEDULED WITH THE REST OF YOUR PROCESSING.');
        }
      }
    }
  }

  update(dt: number): void {
    this.t += dt;
    this.chyron.update(dt);
    this.flap.update(dt);

    // dispenser pilot lamp: STAMP RED pulse (the first thing the dark shows you)
    const pulse = 0.55 + 0.45 * Math.sin(this.t * (Math.PI * 2) / 2.2);
    (this.world.pilotLamp.material as MeshBasicMaterial).color
      .setHex(PAL.stampRed)
      .convertSRGBToLinear()
      .multiplyScalar(0.4 + pulse);

    // vault beacon sweep
    this.beaconPivot.rotation.y += dt * 0.55;

    // vault mouth glow: eased toward its one warm step, then a slow breath on top
    const mouthMat = this.world.vaultMouthGlow.material as MeshBasicMaterial;
    const breath = 0.85 + 0.15 * Math.sin(this.t * 1.3);
    const mouthTarget = (0.05 + this.vaultMouthOn * 0.17) * breath;
    mouthMat.opacity += (mouthTarget - mouthMat.opacity) * Math.min(1, dt * 3);

    // copier REISSUE: a ~2.6s beat — pilots light in sequence, the scan bar
    // brightens and sweeps the lid, then everything returns cold.
    if (this.reissueT >= 0) {
      this.reissueT += dt;
      const pilots = this.world.copierPilots;
      const base = [shade(PAL.amber, -0.55), shade(PAL.amber, -0.55), shade(PAL.stampRed, -0.5)];
      const hot = [PAL.amber, PAL.amber, PAL.stampRed];
      for (let i = 0; i < pilots.length; i++) {
        const on = this.reissueT > 0.3 + i * 0.5 && this.reissueT < 2.4;
        (pilots[i].material as MeshBasicMaterial).color.setHex(on ? hot[i] : base[i]).convertSRGBToLinear();
      }
      const scanMat = this.world.copierScan.material as MeshBasicMaterial;
      const sweeping = this.reissueT > 0.3 && this.reissueT < 2.2;
      scanMat.color.setHex(sweeping ? PAL.gas : shade(PAL.gas, -0.4)).convertSRGBToLinear();
      // the bar tracks across the lid glass (stepped — printers do not ease)
      const k = Math.max(0, Math.min(1, (this.reissueT - 0.3) / 1.9));
      this.world.copierScan.position.z = COPIER.z + 0.56 - Math.floor(k * 10) / 10 * 1.0;
      if (this.reissueT > 2.6) {
        this.reissueT = -1;
        scanMat.color.setHex(shade(PAL.gas, -0.4)).convertSRGBToLinear();
        this.world.copierScan.position.z = COPIER.z + 0.56;
        for (let i = 0; i < pilots.length; i++) {
          (pilots[i].material as MeshBasicMaterial).color.setHex(base[i]).convertSRGBToLinear();
        }
      }
    }

    // turnstile: snaps 120 degrees when the player passes through the throat
    const px = this.player.pos.x;
    const pz = this.player.pos.z;
    const nearGate = Math.abs(pz - TURNSTILE.z) < 1.2 && Math.abs(px - TURNSTILE.x) < 1.4;
    const isEast = px > TURNSTILE.x;
    if (nearGate && isEast !== this.playerWasEast) {
      this.turnstileTarget += (Math.PI * 2) / 3;
      this.bus.emit('audio:thunk');
    }
    this.playerWasEast = isEast;
    if (this.turnstileAngle < this.turnstileTarget) {
      // full speed into a hard stop; no easing
      this.turnstileAngle = Math.min(this.turnstileTarget, this.turnstileAngle + dt * 9);
      this.world.turnstileArms.rotation.y = this.turnstileAngle;
    }

    void DISPENSER;
  }
}
